/**
 * draw.io / mxGraph XML Import Modal
 * Paste draw.io XML → parse → (auto-layout only if geometry missing) → load.
 */

import { useMemo, useState, memo } from 'react'
import Modal from './Modal'
import { parseDrawioToDiagram } from '../../utils/drawioImport'
import type { DiagramData } from '../../types/schema'
import './JSONImportModal.css'

export interface DrawioImportModalProps {
  isOpen: boolean
  onClose: () => void
  onImport: (data: DiagramData) => void
}

const PLACEHOLDER = `<mxGraphModel>
  <root>
    <mxCell id="0"/>
    <mxCell id="1" parent="0"/>
    <mxCell id="2" value="Start" vertex="1" style="rounded=1;" parent="1">
      <mxGeometry x="40" y="40" width="120" height="60" as="geometry"/>
    </mxCell>
    <mxCell id="3" value="End" vertex="1" style="ellipse;" parent="1">
      <mxGeometry x="240" y="40" width="80" height="80" as="geometry"/>
    </mxCell>
    <mxCell id="e1" edge="1" source="2" target="3" parent="1"/>
  </root>
</mxGraphModel>`

function DrawioImportModal({ isOpen, onClose, onImport }: DrawioImportModalProps) {
  const [text, setText] = useState('')

  const result = useMemo(() => {
    if (!text.trim()) return null
    return parseDrawioToDiagram(text)
  }, [text])

  const nodeCount = result?.data.nodes.length ?? 0
  const edgeCount = result?.data.edges?.length ?? 0
  const canImport = !!result && nodeCount > 0

  const handleClose = () => {
    setText('')
    onClose()
  }

  const handleImport = () => {
    if (!result || nodeCount === 0) return
    onImport(result.data)
    handleClose()
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Import from draw.io (XML)">
      <div className="json-import-modal">
        <div className="json-import-content">
          <textarea
            className="json-input-textarea"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={`Paste draw.io / mxGraph XML…\n\n${PLACEHOLDER}`}
            spellCheck={false}
          />
        </div>

        {result && (
          <div className="json-validation-info">
            <div className="json-stats">
              <span>{nodeCount > 0 ? '✅' : '⚠️'} Parsed draw.io XML</span>
              <span>• {nodeCount} nodes</span>
              <span>• {edgeCount} edges</span>
            </div>
            {result.warnings.length > 0 && (
              <div className="json-error" style={{ marginTop: 8 }}>
                <strong>Notes:</strong>
                <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
                  {result.warnings.slice(0, 5).map((w, i) => (
                    <li key={i}>{w}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        <div className="modal-actions">
          <button onClick={handleClose} className="button-secondary">
            Cancel
          </button>
          <button onClick={handleImport} className="button-primary" disabled={!canImport}>
            Import
          </button>
        </div>
      </div>
    </Modal>
  )
}

export default memo(DrawioImportModal)
